import { useQuery, type QueryClient } from '@tanstack/react-query';
import { useAppSelector } from '@doccareplus/kernel/application/store/hooks';
import { selectActiveContext } from '@doccareplus/kernel/application/store/slices/auth.slice';
import { clinicAccessApi } from '../infrastructure/clinicAccess.api';

const CLINIC_ACCESS_STALE_TIME = 60_000;

/** Single source of truth for the clinic-access query key, keyed by active context. */
export const clinicAccessKeys = {
  byContext: (clinicId?: string, role?: string) => ['clinic-access', clinicId, role] as const,
};

export function useClinicAccessQuery() {
  const context = useAppSelector(selectActiveContext);
  return useQuery({
    queryKey: clinicAccessKeys.byContext(context?.clinicId, context?.role),
    queryFn: () => clinicAccessApi.getAccess(),
    enabled: Boolean(context?.clinicId && context?.role),
    staleTime: CLINIC_ACCESS_STALE_TIME,
  });
}

/** The clinic-facing "Manage subscription" page's own read of its current plan/status/commercial terms. */
export function useSubscriptionQuery() {
  const context = useAppSelector(selectActiveContext);
  return useQuery({
    queryKey: ['clinic-subscription', context?.clinicId] as const,
    queryFn: () => clinicAccessApi.getSubscription(),
    enabled: Boolean(context?.clinicId && context?.role),
    staleTime: CLINIC_ACCESS_STALE_TIME,
  });
}

/** The clinic's own read-only SaaS billing history, shown alongside its subscription details (see BP-2). */
export function useBillingHistoryQuery() {
  const context = useAppSelector(selectActiveContext);
  return useQuery({
    queryKey: ['clinic-billing-history', context?.clinicId] as const,
    queryFn: () => clinicAccessApi.getBillingHistory(),
    enabled: Boolean(context?.clinicId && context?.role),
    staleTime: CLINIC_ACCESS_STALE_TIME,
  });
}

/**
 * Fetch (or reuse cached) clinic access for a context and populate the query
 * cache, so the shell's `useClinicAccessQuery` reads it instantly. Used by the
 * workspace bootstrap step. No-op when there is no active context.
 */
export function ensureClinicAccess(
  queryClient: QueryClient,
  context: { clinicId?: string; role?: string } | null | undefined
): Promise<unknown> {
  if (!context?.clinicId || !context?.role) return Promise.resolve(null);
  return queryClient.ensureQueryData({
    queryKey: clinicAccessKeys.byContext(context.clinicId, context.role),
    queryFn: () => clinicAccessApi.getAccess(),
    staleTime: CLINIC_ACCESS_STALE_TIME,
  });
}
